// AR 오버레이 편집(FR-AR-08) — 인식된 페이지 위에 뜰 "움직이는 것"을 지정한다.
// 폴백 사다리 Spine → 투명영상 → 스트립(→ 포스터). 위치·크기·움직임(이동)은 어느 게 뜨든 동일 적용.
// idKey = 장면 식별자(책 페이지면 "<bookId>.<pageId>"). 키: ar.overlay.<idKey> 등.
import {
  useState } from "react";
import { AR_VIDEO_FORMATS,
  arVideoFormat,
  arVideoNote,
  type AssetSpec,
  arOverlayKey,
  arVideoKey,
  arPosterKey,
  arSpineKey,
  arSpineTexPrefix,
  arModelKey,
  arTargetKey,
  AR_INTERACT_LIMITS,
  MODEL_WARN_KB,
  classLoadSeconds,
} from "@greg-chick/shared";
import { absArtUrl, uploadAsset, type ManifestEntry } from "../lib/contentApi";
import { gifToStrip, gifConvertSupported } from "../lib/gifToStrip";
import { SlotUploader } from "./SlotUploader";
import { Model3dMini } from "./Model3dMini";
import { ModelFilePicker } from "./ModelFilePicker";
import { useKb } from "../lib/useKb";

const OVERLAY_SPEC: AssetSpec = { formats: ["webp", "png"], alphaRequired: true, maxLongEdgePx: 4096 };
/**
 * 🎬 **WebM 과 MP4 를 다 받는다.**
 *
 * 예전엔 WebM 만 받았다 — 배경을 지울 수 있는 게 그것뿐이라서. 그런데 **사파리(아이폰)는
 * WebM 을 못 튼다.** 아이폰에서 영상을 보여 주려면 MP4 를 올릴 수 있어야 한다.
 *
 * 대신 무엇이 달라지는지 화면에 적는다(`arVideoNote`) — 올려 놓고 「왜 네모나지」,
 * 「왜 아이폰에서만 안 나오지」로 헤매지 않게. 어느 쪽이 나은지는 콘텐츠가 정한다.
 */
const VIDEO_SPEC: AssetSpec = { formats: [...AR_VIDEO_FORMATS], maxKB: 12000 };
// 포스터는 장면당 한 장(ar.poster.<장면>). 예전엔 [기타 이미지]에 `ar.poster.default` 한 칸뿐이었는데,
// 웹은 그 키를 안 읽는다 — 올려도 아무 데도 안 나오는 칸이었다. 장면 옆으로 옮긴다.
const POSTER_SPEC: AssetSpec = { formats: ["webp", "png"], maxKB: 300 };

const MOTIONS: { id: string; label: string; hint: string }[] = [
  { id: "none", label: "제자리", hint: "그 자리에서(스트립·Spine 자체 동작만)" },
  { id: "float", label: "둥실둥실", hint: "위아래로 부드럽게" },
  { id: "walk", label: "가로질러", hint: "좌우로 왕복하며 이동" },
  { id: "rise", label: "솟아오름", hint: "아래에서 위로 떠오름" },
];

interface Props {
  idKey: string;
  title: string; // 알트·라벨용
  assets: Record<string, ManifestEntry>;
  setEntry: (key: string, entry: ManifestEntry | null) => void;
}

export function ArOverlayEditor({ idKey, title, assets, setEntry }: Props) {
  const overlayKey = arOverlayKey(idKey);
  const videoKey = arVideoKey(idKey);
  const posterKey = arPosterKey(idKey);
  const spineJsonKey = arSpineKey(idKey, "json");

  // 위치·크기·이동 — 존재하는 오버레이 계열 엔트리 전부에 같은 값(우선순위 무엇이 이기든 동일 배치)
  const modelKey = arModelKey(idKey);
  // 🧊 ④ 3D 가 맨 앞 — 아이 화면(arData)이 변환을 읽는 순서와 같다
  const family = [modelKey, spineJsonKey, videoKey, overlayKey];
  const firstWith = <T,>(pick: (e: ManifestEntry) => T | undefined): T | undefined => {
    for (const k of family) {
      const e = assets[k];
      if (e) {
        const v = pick(e);
        if (v !== undefined) return v;
      }
    }
    return undefined;
  };
  const motion = firstWith((e) => e.motion) ?? "none";
  const posX = firstWith((e) => e.pos?.x) ?? 0;
  const posY = firstWith((e) => e.pos?.y) ?? 0;
  const oz = firstWith((e) => e.z) ?? 0.12; // 페이지와의 간격(깊이)
  const oscale = firstWith((e) => e.scale) ?? 1;
  const hasOverlay = family.some((k) => assets[k]);
  const patch = (fn: (e: ManifestEntry) => ManifestEntry) => {
    for (const k of family) {
      const e = assets[k];
      if (e) setEntry(k, fn(e));
    }
  };
  const setMotion = (m: string) => patch((e) => ({ ...e, motion: m === "none" ? undefined : m }));
  const setPos = (x: number, y: number) => patch((e) => ({ ...e, pos: x === 0 && y === 0 ? undefined : { x, y } }));
  const setZ = (v: number) => patch((e) => ({ ...e, z: v === 0.12 ? undefined : v })); // 기본값이면 저장 안 함
  const setScale = (v: number) => patch((e) => ({ ...e, scale: v === 1 ? undefined : v }));

  return (
    <div className="ar-ovl">
      <p className="console__hint">
        카메라가 그림을 알아보면 <b>그 위에 무엇이 나타날까요?</b> 아래에서 하나만 넣어도 돼요.
        여러 개 넣으면 좋은 것부터 자동으로 골라요(<b>3D 모델 → Spine → 투명영상 → 연속그림</b>).
        아무것도 안 넣으면 페이지 그림이 살짝 숨쉬듯 움직여요.
      </p>

      {hasOverlay ? (
        <div className="ar-xform">
          <div className="ar-xform__stage" title="페이지 기준 위치·크기">
            <span className="ar-xform__page">페이지</span>
            <span
              className="ar-xform__box"
              style={{ left: `${50 + posX * 33}%`, top: `${50 - posY * 33}%`, width: `${Math.min(90, 26 * oscale)}%`, height: `${Math.min(90, 26 * oscale)}%` }}
            >
              {motion === "float" ? "↕" : motion === "walk" ? "↔" : motion === "rise" ? "↑" : "◎"}
            </span>
          </div>
          <div className="ar-xform__controls">
            <div className="ar-studio__motion">
              <span className="objz__label">움직임</span>
              <div className="objz__btns">
                {MOTIONS.map((m) => (
                  <button key={m.id} type="button" className={motion === m.id ? "on" : ""} title={m.hint} onClick={() => setMotion(m.id)}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="objxform__row">좌우 위치 {posX.toFixed(2)}
              <input type="range" min={-1.5} max={1.5} step={0.05} value={posX} onChange={(e) => setPos(Number(e.target.value), posY)} />
            </label>
            <label className="objxform__row">상하 위치 {posY.toFixed(2)}
              <input type="range" min={-1.5} max={1.5} step={0.05} value={posY} onChange={(e) => setPos(posX, Number(e.target.value))} />
            </label>
            <label className="objxform__row">크기 {oscale.toFixed(2)}×
              <input type="range" min={0.2} max={4} step={0.1} value={oscale} onChange={(e) => setScale(Number(e.target.value))} />
            </label>
            <label className="objxform__row">페이지와의 간격(높이) {oz.toFixed(2)}
              <input type="range" min={0} max={2} step={0.02} value={oz} onChange={(e) => setZ(Number(e.target.value))} />
            </label>
            {posX !== 0 || posY !== 0 || oz !== 0.12 || oscale !== 1 || motion !== "none" ? (
              <button type="button" className="objz__reset" onClick={() => { setPos(0, 0); setZ(0.12); setScale(1); setMotion("none"); }}>위치·크기·간격·움직임 초기화</button>
            ) : null}
          </div>
        </div>
      ) : null}

      <ArModelSlot idKey={idKey} title={title} assets={assets} setEntry={setEntry} />

      <div className="grid" style={{ marginTop: 8 }}>
        <SlotUploader slotKey={overlayKey} label="① 연속 그림(가로로 이어붙인 그림·투명 배경) — 어느 폰에서나 나와요" spec={OVERLAY_SPEC} entry={assets[overlayKey]} onChange={setEntry} alt={`${title} 오버레이`} procedural="안 올리면 페이지 그림이 살짝 움직여요" />
        <div>
          <SlotUploader slotKey={videoKey} label="② 영상(WebM 또는 MP4)" spec={VIDEO_SPEC} entry={assets[videoKey]} onChange={setEntry} alt={`${title} 영상`} kind="video" procedural="안 올리면 ①연속 그림이 나와요" />
          {/* 🔴 올린 파일이 **실제로 어떻게 보일지** 그 자리에 적는다.
              둘은 장단이 정반대다 — 안 적으면 「왜 네모나지」·「왜 아이폰에서만 안 나오지」로 헤맨다. */}
          {arVideoFormat(assets[videoKey]?.url) ? (
            <p className="hint">{arVideoNote(arVideoFormat(assets[videoKey]?.url))}</p>
          ) : (
            <p className="hint">
              <b>WebM</b>은 배경이 지워져 캐릭터만 떠요(아이폰 ✗ → ①연속 그림). <b>MP4</b>는
              아이폰까지 다 나오지만 네모난 영상으로 보여요.
            </p>
          )}
        </div>
        <SlotUploader slotKey={posterKey} label="③ 포스터 한 장 — 카메라가 안 될 때 대신 보여 줘요" spec={POSTER_SPEC} entry={assets[posterKey]} onChange={setEntry} alt={`${title} 포스터`} procedural="안 올리면 그림 없이 안내 글만 나와요" />
      </div>

      <GifImport overlayKey={overlayKey} title={title} setEntry={setEntry} />

      <SpineBundle idKey={idKey} title={title} assets={assets} setEntry={setEntry} />
    </div>
  );
}

/** 아틀라스 텍스트에서 페이지 파일명 추출(.png/.webp/... 로 끝나는 줄). */
function atlasPages(text: string): string[] {
  return text.split(/\r?\n/).map((l) => l.trim()).filter((l) => /\.(png|webp|jpe?g)$/i.test(l));
}

/** Spine 번들 업로더 — .json/.atlas/텍스처(여러 장)를 한 번에 선택·드래그하면 자동 분류·업로드.
 *  멀티페이지 아틀라스 지원. 텍스처는 원본 파일명을 alt 로 저장(웹이 아틀라스 페이지명과 매칭). */
function SpineBundle({ idKey, assets, setEntry }: Props) {
  const jsonKey = arSpineKey(idKey, "json");
  const atlasKey = arSpineKey(idKey, "atlas");
  const texPrefix = arSpineTexPrefix(idKey);
  const legacyPng = arSpineKey(idKey, "png");
  const texKeys = Object.keys(assets)
    .filter((k) => k.startsWith(texPrefix))
    .sort((a, b) => (Number(a.slice(texPrefix.length)) || 0) - (Number(b.slice(texPrefix.length)) || 0));
  const texCount = texKeys.length + (assets[legacyPng] ? 1 : 0);
  const complete = !!assets[jsonKey] && !!assets[atlasKey] && texCount > 0;

  // 인식된 파일 목록(역할·파일명·삭제) — alt 에 원본 파일명 저장
  const nameOf = (k: string, fb: string) => assets[k]?.alt || fb;
  const files: { key: string; role: string; name: string }[] = [];
  if (assets[jsonKey]) files.push({ key: jsonKey, role: "스켈레톤", name: nameOf(jsonKey, "skeleton.json") });
  if (assets[atlasKey]) files.push({ key: atlasKey, role: "아틀라스", name: nameOf(atlasKey, "skeleton.atlas") });
  texKeys.forEach((k, i) => files.push({ key: k, role: `텍스처 ${i + 1}`, name: nameOf(k, "texture.png") }));
  if (assets[legacyPng]) files.push({ key: legacyPng, role: "텍스처(구형)", name: nameOf(legacyPng, "texture.png") });

  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [status, setStatus] = useState<{ kind: "info" | "ok" | "err"; msg: string } | null>(null);

  async function uploadText(key: string, f: File) {
    const forced = new File([f], f.name, { type: "application/octet-stream" }); // 엣지 화이트리스트 통과
    const url = await uploadAsset(key, forced);
    setEntry(key, { url, alt: f.name }); // alt=원본 파일명(목록 표시용)
  }

  async function handleFiles(fileList: FileList | null) {
    const files = Array.from(fileList ?? []);
    if (!files.length) return;
    const json = files.find((f) => /\.json$/i.test(f.name));
    const atlas =
      files.find((f) => /\.atlas(\.txt)?$/i.test(f.name)) ?? files.find((f) => /\.txt$/i.test(f.name) && f !== json);
    const imgs = files.filter((f) => /\.(png|webp|jpe?g)$/i.test(f.name));
    if (!json && !atlas && !imgs.length) {
      setStatus({ kind: "err", msg: "Spine 파일(.json/.atlas/이미지)이 없어요." });
      return;
    }
    setBusy(true);
    setStatus({ kind: "info", msg: "올리는 중…" });
    try {
      if (json) await uploadText(jsonKey, json);
      if (atlas) await uploadText(atlasKey, atlas);
      if (imgs.length) {
        for (const k of texKeys) setEntry(k, null); // 재업로드면 기존 텍스처 교체
        setEntry(legacyPng, null);
        for (let i = 0; i < imgs.length; i++) {
          const url = await uploadAsset(`${texPrefix}${i}`, imgs[i]!);
          setEntry(`${texPrefix}${i}`, { url, alt: imgs[i]!.name }); // alt=원본 파일명(아틀라스 매칭)
        }
      }
      // 아틀라스가 참조하는 페이지 수와 올린 텍스처 수 대조(경고만)
      const atlasText = atlas ? await atlas.text() : "";
      const pages = atlasText ? atlasPages(atlasText) : [];
      const got = imgs.length;
      const parts = [json ? "json✓" : "", atlas ? "atlas✓" : "", got ? `텍스처 ${got}장` : ""].filter(Boolean).join(" · ");
      const warn = pages.length && got && pages.length !== got ? ` ⚠️ 아틀라스는 ${pages.length}장을 참조하는데 ${got}장 올렸어요.` : "";
      setStatus({ kind: warn ? "err" : "ok", msg: `${parts} 인식됨. 저장하면 반영.${warn}` });
    } catch (e) {
      setStatus({ kind: "err", msg: (e as Error).message || "업로드 실패" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="world-zone" style={{ marginTop: 8 }}>
      <h3 className="world-zone__title">
        <span>③ Spine 애니메이션 — 가장 부드럽게 움직여요</span>
        <span className={`slot__badge${complete ? " slot__badge--real" : ""}`}>
          {complete ? `완성(텍스처 ${texCount}장)` : "파일 필요"}
        </span>
      </h3>
      <p className="console__hint">
        Spine 4.x 내보내기 파일을 <b>한 번에 모두 선택(또는 끌어놓기)</b>하면 자동으로 분류돼요 —
        <code>.json</code> + <code>.atlas</code> + 텍스처 <b>여러 장 가능</b>. (브라우저 특성상 파일 하나만
        고르면 나머지는 자동으로 못 들어와요 → 전부 함께 선택하세요.)
      </p>
      <div
        className={`spine-drop${over ? " spine-drop--over" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); void handleFiles(e.dataTransfer.files); }}
      >
        <label className="slot__file">
          {busy ? "올리는 중…" : "Spine 파일 전체 선택(.json·.atlas·텍스처)"}
          <input type="file" multiple accept=".json,.atlas,.txt,image/*" hidden disabled={busy} onChange={(e) => void handleFiles(e.target.files)} />
        </label>
        <span className="spine-drop__hint">또는 여기로 파일들을 끌어놓기</span>
      </div>
      {files.length ? (
        <ul className="spine-files">
          {files.map((f) => (
            <li key={f.key} className="spine-files__item">
              <span className="spine-files__role">{f.role}</span>
              <span className="spine-files__name" title={f.name}>{f.name}</span>
              <button type="button" className="spine-files__del" title="이 파일 삭제" onClick={() => setEntry(f.key, null)}>✕</button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="zone__tip">아직 올린 Spine 파일이 없어요.</p>
      )}
      <div className="spine-status">
        <span className={`slot__badge${assets[jsonKey] ? " slot__badge--real" : ""}`}>json {assets[jsonKey] ? "✓" : "—"}</span>
        <span className={`slot__badge${assets[atlasKey] ? " slot__badge--real" : ""}`}>atlas {assets[atlasKey] ? "✓" : "—"}</span>
        <span className={`slot__badge${texCount ? " slot__badge--real" : ""}`}>텍스처 {texCount}장</span>
      </div>
      {status ? <p className={`bk-ar__status bk-ar__status--${status.kind}`}>{status.msg}</p> : null}
    </div>
  );
}

/** GIF 업로드 — WebGL 은 GIF 첫 프레임만 읽어 애니가 안 나오므로, 업로드 시 브라우저에서
 *  프레임을 풀어 가로 스트립(webp)+fps 로 자동 변환해 ① 스트립 슬롯에 넣는다(재생 경로 무수정). */
function GifImport({
  overlayKey,
  title,
  setEntry,
}: {
  overlayKey: string;
  title: string;
  setEntry: (key: string, entry: ManifestEntry | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: "info" | "ok" | "err"; msg: string } | null>(null);

  async function handleGif(f: File | null) {
    if (!f) return;
    setBusy(true);
    setStatus({ kind: "info", msg: "GIF 프레임을 풀어 스트립으로 변환 중…" });
    try {
      const strip = await gifToStrip(f);
      const webp = new File([strip.blob], f.name.replace(/\.gif$/i, "") + "_strip.webp", { type: "image/webp" });
      const url = await uploadAsset(overlayKey, webp);
      setEntry(overlayKey, { url, alt: `${title} 오버레이(GIF 변환)`, fps: strip.fps });
      setStatus({
        kind: "ok",
        msg: `✓ 그림 ${strip.frames}장짜리 연속 그림으로 바꿔 ①에 넣었어요. [💾 저장]하면 나와요. (아이 눈 보호를 위해 천천히 재생돼요)`,
      });
    } catch (e) {
      setStatus({ kind: "err", msg: (e as Error).message || "GIF 변환 실패" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gif-import">
      <label className="slot__file">
        {busy ? "변환 중…" : "🎞 GIF로 올리기(자동 스트립 변환)"}
        <input
          type="file"
          accept="image/gif"
          hidden
          disabled={busy || !gifConvertSupported()}
          onChange={(e) => {
            void handleGif(e.target.files?.[0] ?? null);
            e.target.value = ""; // 같은 파일 재선택 허용
          }}
        />
      </label>
      <span className="gif-import__hint">
        움직이는 GIF를 넣으면 ① 연속 그림으로 저절로 바뀌어 애니가 나와요.
        {!gifConvertSupported() ? " (이 브라우저 미지원 — 크롬/엣지 사용)" : ""}
      </span>
      {status ? <p className={`bk-ar__status bk-ar__status--${status.kind}`}>{status.msg}</p> : null}
    </div>
  );
}

/**
 * 🧊 **④ 3D 모델** — `.glb` 를 올리면 카메라가 이 쪽(카드)을 알아볼 때 **그 위에 실물 3D** 가 서고,
 * 아이가 손으로 돌리고(끌기)·기울이고(위아래)·키운다(두 손가락) (2026-09-14 운영자 요청).
 *
 * ## 따로 짜지 않는다
 * 아이 화면은 이 파일로 「3D 층 하나짜리 마법」을 만든다(shared `arModelMagic`) — 28쪽 수막새와 **같은 길**
 * (무대·손짓·표시·출처). 위치·크기·간격은 위 조절(①②③ 과 같은 값)을 그대로 탄다.
 * ## 고르는 순서
 * 이 쪽에 🪄 마법이 있으면 마법이 먼저다(그래서 그땐 여기가 아니라 마법 꾸미기의 3D 칸을 가리킨다).
 * 3D 를 못 불러오면 ①~③ 중 넣어 둔 것으로 돌아간다.
 *
 * 🔴 `.glb` 는 브라우저가 `File.type` 을 비워 보낸다 — `model/gltf-binary` 를 붙여야 엣지가 받는다.
 * 🔴 원본이 **그대로 아이 폰에 간다** — 5MB 넘으면 경고, 20MB 넘으면 안 받는다(shared 문턱).
 */
export function ArModelSlot({ idKey, title, assets, setEntry }: Props) {
  const key = arModelKey(idKey);
  const entry = assets[key];
  const kb = useKb(entry?.url);
  const hasMagic = !!assets[arTargetKey(idKey)]?.interact;
  /** 올리고 자리에 적는다 — 위치·크기·출처는 그대로 두고 파일만 바꾼다. 무엇을 올릴지(줄일지)는 `ModelFilePicker`. */
  async function upload(file: File) {
    const url = await uploadAsset(key, file);
    setEntry(key, { ...(entry ?? {}), url, alt: file.name });
  }
  function setCredit(v: string) {
    if (!entry) return;
    const { credit_ko: _old, ...rest } = entry;
    setEntry(key, v.trim() ? { ...rest, credit_ko: v } : rest);
  }
  return (
    <div className="world-zone" data-ar-model style={{ marginTop: 8 }}>
      <h3 className="world-zone__title">
        <span>④ 3D 모델(.glb) — 비추면 실물이 서고, 손으로 돌려 봐요</span>
        <span className={`slot__badge${entry?.url ? " slot__badge--real" : ""}`}>{entry?.url ? "올라옴" : "선택"}</span>
      </h3>
      <p className="console__hint">
        아이가 끌면 돌고, 위아래로 끌면 기울고, 두 손가락으로 벌리면 커져요. 위치·크기·간격은 위 조절을 그대로 따라요.
        3D 를 못 불러오면 ①~③ 중 넣어 둔 것으로 보여 줘요.
      </p>
      {hasMagic ? (
        <p className="hint" data-ar-model-magic>
          🪄 이 쪽엔 「누르면 일어나는 마법」이 있어 마법이 먼저 떠요. 3D 는 마법 꾸미기의 [🧊 3D 파일] 칸에 넣어 주세요.
        </p>
      ) : null}
      <div className="magicedit__row">
        {entry?.url ? (
          <span className="zone__tip">
            ✓ {entry.alt ?? "올라옴"}{" "}
            <button type="button" className="slot__remove" onClick={() => setEntry(key, null)} aria-label={`${title} 3D 모델 지우기`}>
              지우기
            </button>
          </span>
        ) : (
          <ModelFilePicker label={title} onUpload={upload} />
        )}
      </div>
      {/* 🧊 올린 3D 를 그 자리에서 돌려 본다 — 아이 화면과 같은 무대·손짓(누르기 전엔 엔진을 안 싣는다) */}
      {entry?.url ? <Model3dMini url={absArtUrl(entry.url)} credit={entry.credit_ko} label={title} /> : null}
      {entry?.url && kb !== null ? (
        <p className="hint" data-model-kb>
          {kb > MODEL_WARN_KB ? "🐘 " : ""}이 3D 파일 <b>{kb >= 1024 ? `${(kb / 1024).toFixed(1)}MB` : `${Math.round(kb)}KB`}</b> — 교실 회선에서{" "}
          <b>{classLoadSeconds(kb).toFixed(1)}초</b>쯤 걸려요.
        </p>
      ) : null}
      <label className="bk-field">
        🏷 출처 표기 <span className="zone__tip">(남의 자료를 쓰면 꼭 · 화면 아래에 붙어요)</span>
        <input
          value={entry?.credit_ko ?? ""}
          maxLength={AR_INTERACT_LIMITS.credit}
          disabled={!entry}
          placeholder="예: 국가유산청(KHS) 3D 스캔 · CC BY 4.0"
          aria-label={`${title} 3D 모델 출처 표기`}
          onChange={(e) => setCredit(e.target.value)}
        />
      </label>
    </div>
  );
}
